"use client";

import React, { useState } from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Info, CircleCheck, CircleAlert } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const myths = [{
  id: 1,
  myth: "Sugar is the only cause of cavities.",
  fact: "Cavities are caused by acid-producing bacteria that feed on carbohydrates. While sugar is a major culprit, starchy foods like chips, bread, and crackers can be just as damaging if they stick to teeth.",
  icon: "sugar"
}, {
  id: 2,
  myth: "You should brush harder to clean better.",
  fact: "Brushing too hard can wear down enamel and cause gum recession. A soft-bristled toothbrush with gentle, circular motions is far more effective and safer for your long-term oral health.",
  icon: "brush"
}, {
  id: 3,
  myth: "White teeth are always healthy teeth.",
  fact: "Natural tooth enamel varies in shade and isn't pure white. Extremely white teeth can still have cavities or gum disease, while slightly yellow teeth can be perfectly healthy and strong.",
  icon: "white"
}, {
  id: 4,
  myth: "Bleeding gums are normal when brushing.",
  fact: "Healthy gums do not bleed. Bleeding is often an early sign of gingivitis or gum disease. If your gums bleed, it's a signal to improve your flossing routine and see a dentist, not to ignore it.",
  icon: "blood"
}, {
  id: 5,
  myth: "Baby teeth don't matter since they fall out.",
  fact: "Primary teeth act as essential placeholders for adult teeth. Losing them too early due to decay can cause crowding and alignment issues later, impacting speech, eating, and future dental health.",
  icon: "baby"
}, {
  id: 6,
  myth: "You only need to see a dentist if you have pain.",
  fact: "Many dental issues, including cavities and gum disease, are painless in their early stages. Regular check-ups allow for preventive care, saving you from complex and expensive procedures later.",
  icon: "pain"
}];
export default function MythVsReality() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="myth-vs-reality" ref={ref} className={`dark bg-background text-foreground py-24 overflow-hidden transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-16 text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center justify-center p-2 mb-4 rounded-full bg-accent/10 text-accent-foreground">
            <Info className="w-4 h-4 mr-2" />
            <span className="text-sm font-medium tracking-wide uppercase">Evidence-Based Education</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-serif font-medium tracking-tight mb-6 text-foreground">
            Separating <span className="text-destructive italic">Myth</span> from <span className="text-[var(--chart-3)] italic">Reality</span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Dental health is filled with misconceptions. We break down common myths with clinical facts to help you make informed decisions about your oral care.
          </p>
        </div>

        <div className="relative px-4 md:px-12">
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-6 pb-12">
              {myths.map((item, i) => <CarouselItem key={item.id} data-index={i} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3 xl:basis-1/4 h-[420px]">
                  <div className="group h-full w-full [perspective:1000px] cursor-pointer">
                    <div className="relative h-full w-full transition-all duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                      
                      {}
                      <Card className="absolute inset-0 h-full w-full [backface-visibility:hidden] border-border/50 bg-card overflow-hidden">
                        {}
                        <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmZmIi8+CjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiMwMDAiLz4KPC9zdmc+')]"></div>
                        
                        <CardContent className="flex flex-col items-center justify-center h-full p-8 text-center relative z-10">
                          <div className="mb-6 p-4 rounded-full bg-destructive/10 text-destructive">
                            <CircleAlert className="w-8 h-8" />
                          </div>
                          <h3 className="text-sm font-bold tracking-widest text-destructive mb-4 uppercase">
                            Common Myth
                          </h3>
                          <p className="text-2xl md:text-3xl font-serif font-medium leading-tight text-foreground">
                            &ldquo;{item.myth}&rdquo;
                          </p>
                          <div className="absolute bottom-8 text-xs text-muted-foreground font-medium tracking-widest uppercase opacity-60">
                            Hover to reveal truth
                          </div>
                        </CardContent>
                        {}
                        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-destructive/5 to-transparent pointer-events-none" />
                      </Card>

                      {}
                      <Card className="absolute inset-0 h-full w-full [backface-visibility:hidden] [transform:rotateY(180deg)] bg-secondary border-[var(--chart-3)]/30 overflow-hidden">
                        <CardContent className="flex flex-col items-center justify-center h-full p-8 text-center relative z-10">
                          <div className="mb-6 p-4 rounded-full bg-[var(--chart-3)]/10 text-[var(--chart-3)]">
                            <CircleCheck className="w-8 h-8" />
                          </div>
                          <h3 className="text-sm font-bold tracking-widest text-[var(--chart-3)] mb-4 uppercase">
                            Clinical Fact
                          </h3>
                          <p className="text-base md:text-lg leading-relaxed text-secondary-foreground/90">
                            {item.fact}
                          </p>
                        </CardContent>
                        {}
                        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[var(--chart-3)]/10 to-transparent pointer-events-none" />
                      </Card>

                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            
            {}
            <div className="hidden md:block">
              <CarouselPrevious className="h-14 w-14 border-muted-foreground/20 bg-transparent hover:bg-accent hover:text-accent-foreground hover:border-accent text-muted-foreground -left-16 transition-all duration-300" />
              <CarouselNext className="h-14 w-14 border-muted-foreground/20 bg-transparent hover:bg-accent hover:text-accent-foreground hover:border-accent text-muted-foreground -right-16 transition-all duration-300" />
            </div>
            
            {}
            <div className="flex md:hidden justify-center gap-4 mt-4">
               <CarouselPrevious className="static translate-y-0 h-12 w-12 border-muted-foreground/20 bg-transparent" />
               <CarouselNext className="static translate-y-0 h-12 w-12 border-muted-foreground/20 bg-transparent" />
            </div>

          </Carousel>
        </div>
      </div>
    </section>;
}